import moment from 'moment-timezone';

import {DATE_WITH_TIME_FORMAT} from "../config/DateTimeConstants";
import {BookingStatus} from "../domain/BookingStatus";
import {PERFORMER_CHECK_IN_STATUS} from "../domain/PerformerCheckInStatus";
import {MOMENT_UNIT_MINUTES} from "../domain/ShiftPlanConstants";
import {SESSION_TYPE} from "../domain/SessionType";

export const VIEW_TYPES = {
    DEFAULT: 'default',
    GRID: 'grid'
};

export const NO_UPCOMING_SESSIONS_MESSAGE = 'No upcoming sessions in the given date range';
export const NO_SESSION_HISTORY_MESSAGE = "No session history available";

export const SESSION_JOIN_STATUS_MESSAGE = {
    SESSION_NOT_STARTED: 'Session not started',
    MEMBER_JOINED: 'Member joined',
    MEMBER_DID_NOT_JOIN: 'Member didn\'t join',
    WAITING_FOR_MEMBER_JOIN: 'Member not joined yet',
};

export const STATUS_COLOR_CODES = {
    DEFAULT: 'default',
    SUCCESS: 'success',
    ERROR: 'error',
};

export const MAX_CHILDREN_TO_DISPLAY = 3;

export const SESSION_SCOPE = {
    PAST: 'PAST',
    LIVE: 'LIVE',
    UPCOMING: 'UPCOMING',
};

export const DEFAULT_SESSION_HEALTH_FILTER = 'ALL_SESSIONS';

export const SITE_HEADER_HEIGHT = '6.75rem';
//SITE_HEADER_HEIGHT is hard coded value and needs to be replaced after calculating based on
//outer component height - inner component height.

export const getContentHeight = (windowHeight) => `calc(${windowHeight}px - ${SITE_HEADER_HEIGHT})`;

const PAID_CHECK_IN_WINDOW_START_MARGIN_MINUTES = 30;

export const buildNextUpcomingSessionEvents = (timeslotEvents) => {

    const allEventsExceptCheckIn = timeslotEvents?.filter(event => event.sessionType !== SESSION_TYPE.CHECK_IN);

    const ongoingEvent = allEventsExceptCheckIn?.find(event => moment(event.start).isSameOrBefore(moment()) && moment(event.end).isAfter(moment()));
    const eventsStartingAfterCurrentTime = allEventsExceptCheckIn?.filter(event => moment(event.start).isAfter(moment()));
    const eventsSortedByStartTime = eventsStartingAfterCurrentTime?.sort((a, b) => moment(a.start).diff(moment(b.start)));

    let upcomingSessions = [];

    if (ongoingEvent) {
        upcomingSessions = [ongoingEvent, ...eventsSortedByStartTime?.slice(0, 1)];
    } else {
        upcomingSessions = eventsSortedByStartTime?.slice(0, 2);
    }

    return upcomingSessions;
};

export const buildUpcomingSessions = (upcomingSessionEvents, timeslots) => {
    const upcomingSessions = upcomingSessionEvents?.filter(upcomingSessionEvent => upcomingSessionEvent.isSession)
    .map(upcomingSessionEvent => {
        let foundSession = timeslots.find(session => session.id === upcomingSessionEvent.id);

        if (foundSession?.to) {
            foundSession = {...foundSession, to: getEndTime(foundSession.to)};
        }

        const {
            serviceTypeKeyCode: type,
            primaryColor: color,
            enableLaunchSessionBeforeMinutes,
            disableLaunchSessionAfterMinutes
        } = upcomingSessionEvent;

        foundSession = {
            ...foundSession,
            type,
            color,
            enableLaunchSessionBeforeMinutes,
            disableLaunchSessionAfterMinutes
        };

        return foundSession;
    });

    return upcomingSessions;
};

export const shouldEnableLaunchButton = (selectedEvent, now) => {
    const MINUTES = 'minutes';

    const isZoomLinkCreated = Boolean(selectedEvent?.zoomMeetingUrl);
    const isBookingPresent = selectedEvent?.bookingsCount > 0;
    const elapsedSessionTimeInMinutes = now.diff(selectedEvent.from, MINUTES);

    return (selectedEvent.enableLaunchSessionBeforeMinutes > moment(selectedEvent.from).diff(now, MINUTES)
      && elapsedSessionTimeInMinutes < selectedEvent.disableLaunchSessionAfterMinutes
      && isBookingPresent
      && isZoomLinkCreated);
};

export const buildShiftEvents = (shifts, timezone) =>
  shifts.map(currentShift => {
      return {
          id: currentShift.id,
          start: moment.tz(currentShift.from, timezone).format(DATE_WITH_TIME_FORMAT),
          end: moment.tz(getEndTime(currentShift.to), timezone).format(DATE_WITH_TIME_FORMAT),
          isSession: false,
          performerCheckInTime: currentShift.performerCheckInTime,
          checkInStatus: currentShift.checkInStatus,
          hasCheckInTimeslot: currentShift.hasCheckInTimeslot,
          performingFrom: moment.tz(currentShift.performingFrom, timezone).format(DATE_WITH_TIME_FORMAT),
          primaryColor: currentShift.bookableServiceTypes.map(serviceTypes => serviceTypes.serviceType.primaryColor)
      }
  });

export const buildBookedSessionEvents = (shifts) => {
    return shifts.flatMap(currentShift => {

        const teachingSessions = currentShift.sessions.filter(session => session.isTeachingSession);

        const groupedBySessionId = teachingSessions.reduce((sessionGroups, session) => {
            const sessionId = session.id;

            if (!sessionGroups[sessionId]) {
                sessionGroups[sessionId] = [];
            }

            sessionGroups[sessionId].push(session);
            return sessionGroups;
        }, {});

        return Object.values(groupedBySessionId).map(currentSessionGroup => {
            const firstSession = currentSessionGroup[0];
            const lastSession = currentSessionGroup[currentSessionGroup.length - 1];

            return {...firstSession, from: firstSession.from, to: lastSession.to, shiftId: currentShift.id};
        });
    });
};

export const buildNonTeachingSessionEvents = (shifts) => shifts.flatMap(currentShift => {
    return [
        ...currentShift.sessions
            .filter(currentSession => (
              currentSession.sessionType === SESSION_TYPE.BREAK
              || currentSession.sessionType === SESSION_TYPE.CHECK_IN
              || currentSession.sessionType === SESSION_TYPE.TRAINING))
            .map(session => ({ ...session, shiftId: currentShift.id }))
    ];
});

export const buildMostUpcomingShiftWhichRequiresPerformerCheckIn = (shifts) => {

    const doesShiftRequiresCheckIn = (shift) => shift.hasCheckInTimeslot;
    const isShiftEndTimeInFuture = (shift) => moment(shift.end).isAfter(moment());
    const isPerformerNotCheckedIn = (shift) =>
      shift.checkInStatus === PERFORMER_CHECK_IN_STATUS.PENDING || shift.checkInStatus === PERFORMER_CHECK_IN_STATUS.FAILED;

    const isShiftInCheckInWindow = (shift) =>
      moment().isSameOrAfter(moment(shift.performingFrom).subtract(PAID_CHECK_IN_WINDOW_START_MARGIN_MINUTES, MOMENT_UNIT_MINUTES)) && moment(shift.end).isAfter(moment());

    const upcomingOrOngoingShiftPendingCheckIn = (shift) => isPerformerNotCheckedIn(shift) && isShiftInCheckInWindow(shift);

    const allShiftsWhichArePendingCheckIn = shifts
    ?.filter(shift => doesShiftRequiresCheckIn(shift))
    ?.filter(shift => isPerformerNotCheckedIn(shift))
    ?.filter(shift => isShiftEndTimeInFuture(shift))
    ?.sort((a, b) => moment(a.start).diff(moment(b.start)));

    if (allShiftsWhichArePendingCheckIn
      && allShiftsWhichArePendingCheckIn.length > 0
      && upcomingOrOngoingShiftPendingCheckIn(allShiftsWhichArePendingCheckIn[0])) {

        return allShiftsWhichArePendingCheckIn[0];
    }

    return undefined;
}


export const getEndTime = (time) => {
  const sessionTime = moment(time);

  const isStartOfDay = sessionTime.isSame(sessionTime.clone().startOf('day'));

  return isStartOfDay
    ? sessionTime.subtract(1, 'millisecond').toDate()
    : sessionTime.toDate();
}

export const buildDeletableSessionIds = (event, shiftData, isSessionComplete) => {
    const sessionIds = [];

    if (!event?.from || !event?.to || !isSessionComplete) {
        return sessionIds;
    }

    const currentShiftData = shiftData.filter(item => item.availabilityId === event.availabilityId)
        .sort((a, b) => moment(a.from).diff(moment(b.from)));

    const currentSlotIndex = currentShiftData.findIndex(item => item.id === event.id);

    for (let i = currentSlotIndex; i < currentShiftData.length; i++) {
        const slotData = currentShiftData[i];
        const sessionType = slotData.session?.sessionType;
        const isDeletableSessionType =
          sessionType === SESSION_TYPE.BLOCKED
          || sessionType === SESSION_TYPE.BREAK
          || sessionType === SESSION_TYPE.TRAINING;

        if (isDeletableSessionType) {
            sessionIds.push(slotData.session.id);
        } else {
            break;
        }
    }

    return sessionIds;
};

export const buildChildAgeInYears = (dateOfBirth) => {
    if (!dateOfBirth) {
        return null;
    }
    return moment().diff(dateOfBirth, 'years');
};

export const buildChildDisplayName = (child) => {
    return child?.displayNameRomanized || child?.displayName;
};

export const buildChildBiographicalDetails = (child) => {
    if (!child) {
        return '';
    }

    const childName = buildChildDisplayName(child);
    const childAge = buildChildAgeInYears(child.dateOfBirth);
    const childRegion = child.parent?.region || '?';
    const childDisplayAge = childAge ?? '?';

    return `${childName}, ${childDisplayAge}, ${childRegion}`;
};

export const buildChildBiographicalData = (child) => {
    if (!child) {
        return '';
    }

    return {
        age: buildChildAgeInYears(child.dateOfBirth),
        region: child.parent?.region || '?',
        memberId: child.parent?.externalId
    };
};

export const evaluateSessionTiming = (sessionStartTime, sessionEndTime, timezone) => {
    const now = moment.tz(timezone);
    const sessionStart = moment.tz(sessionStartTime, timezone);
    const sessionEnd = moment.tz(sessionEndTime, timezone);

    const isPastSession = sessionEnd.isBefore(now);
    const isUpcomingSession = sessionStart.isAfter(now);

    return { isPastSession, isUpcomingSession };
};

export const buildMemberStatusColorCode = (status, sessionStartTime, sessionEndTime, timezone) => {
    const {isPastSession} = evaluateSessionTiming(sessionStartTime, sessionEndTime, timezone);
    let colorCode = STATUS_COLOR_CODES.DEFAULT;

    if (status === BookingStatus.ATTENDING || status === BookingStatus.ATTENDED) {
        colorCode = STATUS_COLOR_CODES.SUCCESS;
    } else if (status === BookingStatus.UNATTENDED && isPastSession) {
        colorCode = STATUS_COLOR_CODES.ERROR;
    }

    return colorCode;
};

export const buildMemberStatusMessage = (statusColor, isUpcomingSession) => {
    let message = SESSION_JOIN_STATUS_MESSAGE.WAITING_FOR_MEMBER_JOIN;

    if (statusColor === STATUS_COLOR_CODES.DEFAULT && isUpcomingSession) {
        message = SESSION_JOIN_STATUS_MESSAGE.SESSION_NOT_STARTED;
    } else if (statusColor === STATUS_COLOR_CODES.SUCCESS) {
        message = SESSION_JOIN_STATUS_MESSAGE.MEMBER_JOINED;
    } else if (statusColor === STATUS_COLOR_CODES.ERROR) {
        message = SESSION_JOIN_STATUS_MESSAGE.MEMBER_DID_NOT_JOIN;
    }

    return message;
};

const DEFAULT_TAG_COUNT_TO_DISPLAY = 4;
export const SENTIMENT = {
    POSITIVE: 'POSITIVE',
    NEGATIVE: 'NEGATIVE',
    NEUTRAL: 'NEUTRAL'
};

export const buildTagViewModel = (tag, votes = 0, isSelected = false) => {
    return {
        id: tag.id,
        label: tag.name,
        sentiment: tag.sentiment,
        category: tag.type,
        votes: votes,
        isSelected: isSelected
    };
};

export const buildTopTagsByCount = (tagMetrics, maxTags = DEFAULT_TAG_COUNT_TO_DISPLAY) => {
    if (!tagMetrics || tagMetrics.length === 0) {
        return [];
    }

    return tagMetrics
        .map(tagMetric => buildTagViewModel(tagMetric.tag, tagMetric.count))
        .sort((a, b) => b.votes - a.votes)
        .slice(0, maxTags);
};

// TODO: NEGATIVE and NEUTRAL sentiment currently combined. In future, NEUTRAL may need its own count/color (frontend-only change).
export const buildTagCountsBySentiment = (tagMetrics) => {
    if (!tagMetrics || tagMetrics.length === 0) {
        return {positiveCount: 0, negativeCount: 0};
    }

    const totalCount = tagMetrics.length;

    const positiveCount = tagMetrics.filter(tagMetric => tagMetric.tag.sentiment === SENTIMENT.POSITIVE).length;
    const negativeCount = totalCount - positiveCount;

    return {positiveCount, negativeCount};
};

export const buildUniqueTagCategories = (tags) => {
    if (!tags || tags.length === 0) {
        return [];
    }

    const uniqueTypes = [...new Set(tags.map(tag => tag.type))];
    return uniqueTypes.sort();
};